/**
 * Last line of defence for a render error (ERR-06): instead of a blank white page, a Thai
 * message with a way out. The editor's unsaved edits are already in the local journal (it is
 * written on every change), so reloading offers them back through the recovery dialog.
 * Navigating to another page clears the error, so one broken screen does not lock the app.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from '../lib/lazy.js';

interface State {
  error: Error | null;
  hash: string;
  ref: string;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null, hash: typeof window === 'undefined' ? '' : window.location.hash, ref: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, ref: Math.random().toString(36).slice(2, 10) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[xcreative] render error (ref ${this.state.ref || '-'})`, error, info.componentStack);
  }

  override componentDidMount(): void {
    window.addEventListener('hashchange', this.onHash);
  }

  override componentWillUnmount(): void {
    window.removeEventListener('hashchange', this.onHash);
  }

  private onHash = () => {
    if (this.state.error && window.location.hash !== this.state.hash) this.setState({ error: null, hash: window.location.hash });
    else this.setState({ hash: window.location.hash });
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    // A page chunk that could not be fetched (offline, a stale release) is not a bug: "ลองอีกครั้ง"
    // mounts the page again, which downloads again (lib/lazy.ts).
    const chunk = isChunkLoadError(this.state.error);
    return (
      <div className="xc-fullmsg" role="alert">
        <h1>{chunk ? 'โหลดหน้านี้ไม่สำเร็จ' : 'เกิดข้อผิดพลาดในการแสดงผล'}</h1>
        <p>
          {chunk
            ? 'อาจออฟไลน์อยู่ หรือแอปมีเวอร์ชันใหม่ กด “ลองอีกครั้ง” เมื่อเชื่อมต่อได้แล้ว หรือ “โหลดใหม่” เพื่อรับเวอร์ชันล่าสุด งานที่บันทึกแล้วปลอดภัย'
            : 'งานที่บันทึกแล้วปลอดภัย และการแก้ไขล่าสุดในเครื่องนี้จะถูกเสนอให้กู้คืนเมื่อเปิดงานอีกครั้ง กด “โหลดใหม่” หรือกลับหน้าหลัก'}
        </p>
        {!chunk && <p className="xc-muted">รหัสอ้างอิง {this.state.ref}</p>}
        <div className="xc-session-actions" style={{ justifyContent: 'center' }}>
          {chunk && (
            <button type="button" className="xc-btn xc-btn-primary" onClick={() => this.setState({ error: null })}>
              ลองอีกครั้ง
            </button>
          )}
          <button type="button" className={chunk ? 'xc-btn' : 'xc-btn xc-btn-primary'} onClick={() => window.location.reload()}>
            โหลดใหม่
          </button>
          <a className="xc-btn" href="#/">
            กลับหน้าหลัก
          </a>
        </div>
      </div>
    );
  }
}
